/**
 * @author frank.zhang
 * 2022-06-29
 */
class AccessoriesSales extends HTMLElement {
  constructor() {
    super();

    this.selectedAccessories = [];
    this.selectedProductIds = [];
    this.lastFetchTask = null;
    this.assemblyServiceSelected = false;

    // 默认选中的variant list
    this.defaultSelectedVariantList = [];
    this.matchedVariantByProductId = {};
    this.previewImageByProductId = {};

    // mount this to container
    const targetContainer = document.querySelector('div#' + this.dataset.targetContainerId);
    if (targetContainer) {
      targetContainer.innerHTML = '';
      targetContainer.appendChild(this);
    }

    // 根据URL携带参数默认勾选配件
    const urlParams = new URLSearchParams(window.location.search);
    this.skipAccMode = urlParams.get('skipAcc') === '1';
    if (urlParams.get('selectAccessories') === '1') {
      const accessories = urlParams.get('accessories').split('|||');
      for (const accessory of accessories) {
        const [productId, productHandle, variantId, price] = accessory.split('___');
        const imageUrl =
          typeof getAccessoryVariantImageUrl === 'function'
            ? getAccessoryVariantImageUrl(+productId, +variantId)
            : null;
        this.selectItem(+productId, productHandle, +variantId, +price, '', imageUrl);
      }
    }

    // 自动勾选默认选中的配件
    this.autoSelectDefaultItems();
  }

  // 自动勾选默认选中的配件（ URL 中含 skipAcc=1 时跳过 ）
  autoSelectDefaultItems() {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('skipAcc') === '1') return;

    this.syncAccessoriesToMainVariant(
      typeof getMainProductCurrentVariant === 'function' ? getMainProductCurrentVariant() : window.currentVariant
    );
  }

  syncAccessoriesToMainVariant(mainVariant) {
    if (!mainVariant) return;

    const urlParams = new URLSearchParams(window.location.search);
    const skipAcc = urlParams.get('skipAcc') === '1';
    this.matchCurrentVariant = mainVariant;

    const accessoryItems = this.querySelectorAll('.accessories-list li[data-type=accessory]');
    if (!accessoryItems?.length) return;

    let needsRefresh = false;
    let previewUpdated = false;
    const nextDefaultSelectedVariantList = [];

    const processedProductIds = new Set();

    accessoryItems.forEach((el) => {
      const {
        productId,
        productHandle,
        replaceSelectedColor,
        autoMatchVariantOption,
        accessoryOptionsNames,
      } = el.dataset;
      const numericProductId = Number(productId);
      if (processedProductIds.has(numericProductId)) return;
      processedProductIds.add(numericProductId);

      let parsedAccessoryOptionNames = null;
      if (accessoryOptionsNames) {
        try {
          parsedAccessoryOptionNames = JSON.parse(accessoryOptionsNames);
        } catch {
          parsedAccessoryOptionNames = null;
        }
      }

      const preferredVariantId =
        this.selectedAccessories.find((item) => Number(item.productId) === numericProductId)?.variantId ||
        this.matchedVariantByProductId[numericProductId]?.id;

      const matchParams = {
        mainVariant,
        productId: numericProductId,
        replaceSelectedColor,
        autoMatchVariantOption: autoMatchVariantOption || 'option2',
        accessoryOptionNames: parsedAccessoryOptionNames,
        preferredVariantId,
      };

      const previewVariant =
        typeof findMatchingAccessoryVariant === 'function'
          ? findMatchingAccessoryVariant({ ...matchParams, ignoreUnfit: true })
          : null;

      const selectableVariant =
        typeof findMatchingAccessoryVariant === 'function'
          ? findMatchingAccessoryVariant(matchParams)
          : null;

      if (previewVariant) {
        this.matchedVariantByProductId[numericProductId] = previewVariant;

        const imageUrl =
          typeof getVariantFeaturedImageSrc === 'function' ? getVariantFeaturedImageSrc(previewVariant) : null;
        if (imageUrl) {
          this.previewImageByProductId[numericProductId] = imageUrl;
          previewUpdated = true;
        }
      }

      if (!selectableVariant) return;

      if (el.classList.contains('default-selected')) {
        nextDefaultSelectedVariantList.push({
          productId: numericProductId,
          variantId: +selectableVariant.id,
        });
      }

      const isSelected = this.selectedProductIds.includes(numericProductId);
      const isDefaultSelected = el.classList.contains('default-selected');

      if (isSelected || (isDefaultSelected && !skipAcc)) {
        const imageUrl =
          typeof getVariantFeaturedImageSrc === 'function' ? getVariantFeaturedImageSrc(selectableVariant) : null;
        this.selectItem(
          numericProductId,
          productHandle,
          +selectableVariant.id,
          +selectableVariant.price / 100,
          selectableVariant.sku,
          imageUrl,
          { skipRefresh: true }
        );
        needsRefresh = true;
      }
    });

    this.defaultSelectedVariantList = nextDefaultSelectedVariantList;

    if (needsRefresh || previewUpdated) {
      this.refreshListView();
    }
  }

  selectItem(productId, productHandle, variantId, price, sku, imageUrl = null, options = {}) {
    const numericProductId = Number(productId);
    const resolvedImageUrl =
      imageUrl ||
      (typeof getAccessoryVariantImageUrl === 'function'
        ? getAccessoryVariantImageUrl(numericProductId, variantId)
        : null);
    const existing = this.selectedAccessories.find((d) => Number(d.productId) === numericProductId);

    if (existing) {
      existing.productHandle = productHandle;
      existing.variantId = variantId;
      existing.price = price;
      existing.sku = sku;
      if (resolvedImageUrl) existing.imageUrl = resolvedImageUrl;
    } else {
      this.selectedAccessories.push({
        productId: numericProductId,
        productHandle,
        variantId,
        price,
        sku,
        imageUrl: resolvedImageUrl,
      });
    }

    this.selectedProductIds = this.selectedAccessories.map((d) => d.productId);
    if (!options.skipRefresh) {
      this.refreshListView();
    }
  }

  removeItem(productId) {
    if (this.selectedProductIds.includes(Number(productId))) {
      this.selectedAccessories = this.selectedAccessories.filter((d) => d.productId !== Number(productId));
      this.selectedProductIds = this.selectedAccessories.map((d) => d.productId);
      this.refreshListView();
    }
  }

  getDefaultSelectedProductIds() {
    const ids = new Set();
    this.querySelectorAll('.accessories-list li[data-type=accessory].default-selected').forEach((el) => {
      ids.add(Number(el.dataset.productId));
    });
    return ids;
  }

  resetSelectionAfterAddToCart() {
    if (this.skipAccMode) {
      this.selectedAccessories = [];
      this.selectedProductIds = [];
    } else {
      const defaultProductIds = this.getDefaultSelectedProductIds();
      this.selectedAccessories = this.selectedAccessories.filter((item) =>
        defaultProductIds.has(Number(item.productId))
      );
      this.selectedProductIds = this.selectedAccessories.map((d) => d.productId);
    }
    this.assemblyServiceSelected = false;
    this.refreshListView();

    if (this.modal?.hasAttribute('open') && typeof this.modal.hide === 'function') {
      this.modal.hide();
    }
  }

  updateAccessoryListImages() {
    const listItems = this.querySelectorAll('.accessories-list li[data-type=accessory]');
    listItems.forEach((el) => {
      const { productId, defaultImageSrc } = el.dataset;
      const imageEl = el.querySelector('.accessories-li-image img');
      if (!imageEl) return;

      const numericProductId = Number(productId);
      const selected = this.selectedAccessories.find((item) => Number(item.productId) === numericProductId);
      const previewImageUrl = this.previewImageByProductId?.[numericProductId];
      const isUnfit = el.classList.contains('unfit');

      if (selected?.imageUrl && !isUnfit) {
        const src = selected.imageUrl;
        imageEl.src = `${src}&width=78`;
        imageEl.srcset = `${src}&width=78 1x, ${src}&width=156 2x`;
      } else if (previewImageUrl) {
        imageEl.src = `${previewImageUrl}&width=78`;
        imageEl.srcset = `${previewImageUrl}&width=78 1x, ${previewImageUrl}&width=156 2x`;
      } else if (defaultImageSrc) {
        imageEl.src = defaultImageSrc;
        imageEl.srcset = `${defaultImageSrc} 1x, ${defaultImageSrc.replace('width=300', 'width=600')} 2x`;
      }
    });
  }

  getBundleDiscountConfig() {
    return {
      bundleProductIds: typeof parseBundleProductIds === 'function'
        ? parseBundleProductIds(this.dataset.bundleProductIds)
        : [],
      discountType: this.dataset.bundleDiscountType || 'fixed_amount',
      fixedAmount: parseFloat(this.dataset.bundleDiscountFixedAmount) || 0,
      percentage: Number(this.dataset.bundleDiscountPercentage) || 0,
      showDiscountAmount: this.dataset.bundleShowDiscount === 'true',
      mainProductId: Number(this.dataset.mainProductId) || null,
    };
  }

  updateMainAddToCartButton() {
    const outsideVariantElement = document.querySelector('variant-radios:not([data-tplsuffix="accessory-modal"])');
    if (!outsideVariantElement?.currentVariant) return;

    if (outsideVariantElement.currentVariant.available) {
      outsideVariantElement.toggleAddButton(false);
    }
  }

  updateOuterListOrderAndVisibility() {
    const outerList = this.querySelector('.outer-list-container .accessories-list');
    if (!outerList) return;

    const pcLimit = Number(this.dataset.accessoryShowCount) || 5;
    const mobileLimit = Number(this.dataset.mobileShowCount) || 3;
    const isMobile = window.matchMedia('(max-width: 749px)').matches;
    const limit = isMobile ? mobileLimit : pcLimit;

    const items = Array.from(outerList.querySelectorAll('li'));
    const selectedIdSet = new Set(this.selectedProductIds.map(Number));
    const pinnedTypes = new Set(['gift', 'gift_with_product']);
    const pinnedItems = [];
    const accessoryItems = [];

    items.forEach((el) => {
      if (pinnedTypes.has(el.dataset.type)) {
        pinnedItems.push(el);
      } else {
        accessoryItems.push(el);
      }
    });

    if (isMobile) {
      const mobileEligible = items.filter((el) => !el.classList.contains('hide_on_mobile'));
      const visiblePinnedCount = pinnedItems.filter((el) => !el.classList.contains('hide_on_mobile')).length;
      const maxAccessoryShow = Math.max(0, limit - visiblePinnedCount);
      let accessoryShown = 0;

      items.forEach((el) => {
        if (el.classList.contains('hide_on_mobile')) {
          return;
        }
        const isPinned = pinnedTypes.has(el.dataset.type);
        let show = isPinned;
        if (!show) {
          show = accessoryShown < maxAccessoryShow;
          if (show) accessoryShown += 1;
        }
        el.classList.toggle('foldShow', show);
        el.classList.remove('accessory-outer-collapsed');
      });

      const foldToggle = outerList.parentElement?.querySelector('.foldOrOpenAccessories');
      if (foldToggle) {
        const visibleMobileCount = mobileEligible.filter((el) => el.classList.contains('foldShow')).length;
        const hiddenCount = Math.max(0, mobileEligible.length - visibleMobileCount);
        foldToggle.style.display = hiddenCount > 0 ? '' : 'none';
      }
      return;
    }

    const selectedAccessories = [];
    this.selectedAccessories.forEach((acc) => {
      const el = accessoryItems.find((li) => Number(li.dataset.productId) === Number(acc.productId));
      if (el) selectedAccessories.push(el);
    });

    const unselectedAccessories = accessoryItems.filter((el) => !selectedIdSet.has(Number(el.dataset.productId)));
    const reordered = [...pinnedItems, ...selectedAccessories, ...unselectedAccessories];
    reordered.forEach((el) => outerList.appendChild(el));

    const maxUnselectedToShow = Math.max(0, limit - pinnedItems.length - selectedAccessories.length);
    let unselectedShown = 0;
    const mobileEligible = reordered.filter((el) => !el.classList.contains('hide_on_mobile'));

    reordered.forEach((el) => {
      const isPinned = pinnedTypes.has(el.dataset.type);
      const isSelected = selectedIdSet.has(Number(el.dataset.productId));
      let show = isPinned || isSelected;

      if (!show) {
        show = unselectedShown < maxUnselectedToShow;
        if (show) unselectedShown += 1;
      }

      el.classList.toggle('accessory-outer-collapsed', !show);
    });

    const foldToggle = outerList.parentElement?.querySelector('.foldOrOpenAccessories');
    if (foldToggle) {
      foldToggle.style.display = 'none';
    }
  }

  refreshListView() {
    const listItems = this.querySelectorAll('.accessories-list li[data-type=accessory]');
    listItems.forEach((el) => {
      const { productId } = el.dataset;
      if (this.selectedProductIds.includes(Number(productId))) {
        el.classList.add('selected');
      } else {
        el.classList.remove('selected');
      }
    });

    this.updateAccessoryListImages();
    this.updateOuterListOrderAndVisibility();

    // Assembly service selected state
    const assemblyServiceItems = this.querySelectorAll('.assembly-service-panel[data-type=assembly_service]');
    assemblyServiceItems.forEach((el) => {
      if (this.assemblyServiceSelected) {
        el.classList.add('selected');
      } else {
        el.classList.remove('selected');
      }
    });

    // 展示全部配件按钮上的已选按钮计数
    const selectedCountElement = this.querySelector('.selectedCount');
    const buttonTextElement = this.querySelector('.button_text');
    const buttonTextAfterSelectedElement = this.querySelector('.button_text_after_selected');
    if (selectedCountElement) {
      if (this.selectedAccessories.length) {
        selectedCountElement.classList.remove('hidden');
        buttonTextAfterSelectedElement.classList.remove('hidden');
        buttonTextElement.classList.add('hidden');
        selectedCountElement.querySelector('.selectedCountNum').innerHTML = this.selectedAccessories.length;
      } else {
        selectedCountElement.classList.add('hidden');
        buttonTextAfterSelectedElement.classList.add('hidden');
        buttonTextElement.classList.remove('hidden');
        selectedCountElement.querySelector('.selectedCountNum').innerHTML = 0;
      }
    }

    // 选择或取消选择配件时更新产品信息区价格
    const outsideVariantElement = document.querySelector('variant-radios:not([data-tplsuffix="accessory-modal"])');
    if (outsideVariantElement?.currentVariant) {
      const { price, compare_at_price } = outsideVariantElement.currentVariant;
      const display =
        typeof buildCombinedProductDisplayPrice === 'function'
          ? buildCombinedProductDisplayPrice(price, compare_at_price, this.selectedAccessories, this.dataset.mainProductId)
          : null;
      if (display) {
        outsideVariantElement.updatePrice(
          display.formattedPrice,
          true,
          display.formattedComparePrice,
          display.compareAtCents || 0,
          display.priceCents
        );
      } else {
        const accessoriesTotalPrice = this.selectedAccessories.reduce((prev, curr) => prev + Number(curr.price), 0);
        const comparePrice = compare_at_price
          ? theme.Currency.formatMoney(compare_at_price + accessoriesTotalPrice * 100, shopSettings.moneyFormat)
          : null;
        const actualPrice = price + accessoriesTotalPrice * 100;
        const actualCompareAt = compare_at_price ? compare_at_price + accessoriesTotalPrice * 100 : 0;
        outsideVariantElement.updatePrice(
          theme.Currency.formatMoney(actualPrice, shopSettings.moneyFormat),
          true,
          comparePrice,
          actualCompareAt,
          actualPrice
        );
      }
    }

    this.updateMainAddToCartButton();

    window.UniEventBus.submit('UPDATE_SELECTED_ACCESSORIES', this.selectedAccessories);
    window.UniEventBus.submit('UPDATE_ASSEMBLY_SERVICE', this.assemblyServiceSelected);
  }

  selectAssemblyService() {
    this.assemblyServiceSelected = true;
    this.refreshListView();
  }

  deselectAssemblyService() {
    this.assemblyServiceSelected = false;
    this.refreshListView();
  }

  connectedCallback() {
    // init variables
    this.modal = this.querySelector('modal-dialog');
    this.contentNode = this.querySelector('.product-content .html-content');
    this.loadingBar = this.querySelector('.loading-bar');
    const self = this;

    this.observerModal();

    const handleAccessoryItemOpen = (listItem) => {
      if (!listItem || listItem.classList.contains('checked') || listItem.classList.contains('unfit')) {
        return;
      }
      let inModal = false;
      let curNode = listItem.parentNode;
      while (curNode != self.modal.parentNode) {
        if (curNode.matches('modal-dialog')) {
          inModal = true;
          break;
        }
        curNode = curNode.parentNode;
      }

      const { type } = listItem.dataset;
      const oneline = listItem.hasAttribute('oneline');

      let accessoryElement = listItem;
      /**
       * Auto select first accessory if click gift item.
       */
      if (type === 'gift') {
        if (inModal) {
          return;
        }
        accessoryElement = self.querySelector('.accessories-modal-content li[data-type=gift_with_product]');
      }

      const { productContentUrl: url, productId } = accessoryElement.dataset;

      if (type == 'gift_with_product' || type == 'gift') {
        self.displayItemAccessory(productId, url, true);
      } else {
        self.displayItemAccessory(productId, url, false, oneline);
      }

      /**
       * Auto scroll to center if click item not in modal.
       */
      if (!inModal) {
        self.querySelector(`.accessories-modal-content li[data-product-id="${productId}"]`).scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'center',
        });
      }
    };

    // click add button — always open modal; user confirms selection inside modal
    EventDelegationUtil.on(this, 'click', 'li[data-type=accessory] .add-button', function (e) {
      if (this.classList.contains('unfit')) {
        return;
      }
      const { productVariantCount } = this.dataset;
      if (productVariantCount != 1) {
        // 如果点击的是打开的弹窗中的add-button，那就给底部的select按钮加点动画效果提示用户需要点击下方的select按钮，而不是点击侧边的add按钮
        self.querySelector('.select-item-button--select-addon')?.classList.add('flicker');
        setTimeout(() => {
          self.querySelector('.select-item-button--select-addon')?.classList.remove('flicker');
        }, 500);
      }
      handleAccessoryItemOpen(this.parentNode);
    });

    // click selected button
    EventDelegationUtil.on(this, 'click', 'li[data-type=accessory] .selected-button', function (e) {
      const { productHandle, productId } = this.dataset;
      self.removeItem(productId);
    });

    // click open modal button
    EventDelegationUtil.on(this, 'click', 'button.open-modal-button', function (e) {
      self.querySelector('.outer-list-container li[data-type=accessory]').click();
    });

    // click close modal button
    EventDelegationUtil.on(this, 'click', 'button#accessories-closeTest', function (e) {
      self.modal.click();
    });

    // click assembly service selected button (checkmark) - deselect only
    EventDelegationUtil.on(this, 'click', '.assembly-service-selected-button', function (e) {
      e.stopPropagation();
      self.deselectAssemblyService();
    });

    EventDelegationUtil.on(this, 'click', '.assembly-service-button', function (e) {
      e.stopPropagation();
      self.selectAssemblyService();
    });

    EventDelegationUtil.on(this, 'click', '.assembly-service-card-surface', function (e) {
      if (e.target.closest('.assembly-service-selected-button')) return;
      if (e.target.closest('.assembly-service-details-trigger')) return;
      if (e.target.closest('.assembly-service-button')) return;
      if (e.target.closest('a')) return;
      self.selectAssemblyService();
    });

    EventDelegationUtil.on(this, 'click', '.assembly-service-details-trigger', function (e) {
      e.preventDefault();
      e.stopPropagation();
      const modalId = this.dataset?.assemblyModalId || this.closest('.assembly-service-panel')?.dataset?.assemblyModalId;
      if (!modalId) return;
      const modal = self.querySelector('#' + modalId);
      if (modal && typeof modal.show === 'function') {
        modal.show();
      }
    });

    // close assembly service modal (X button)
    EventDelegationUtil.on(this, 'click', '.assembly-service-close, .assembly-service-close-btn', function (e) {
      const modal = this.closest('modal-dialog');
      if (modal && typeof modal.hide === 'function') {
        modal.hide();
      }
    });

    // click accessory item
    EventDelegationUtil.on(
      this,
      'click',
      'li[data-type=accessory], li[data-type=gift], li[data-type=gift_with_product]',
      function (e) {
        handleAccessoryItemOpen(this);
      },
      '.add-button',
      '.selected-button'
    );

    window.UniEventBus.on('SELECT_ACCESSORY_ITEM', (item) => {
      /**
       * item: {
       *  productId
       *  variantId
       *  productHandle
       *  imageUrl
       * }
       */
      const { productId, productHandle, variantId, variantPrice, sku, imageUrl } = item;
      this.selectItem(productId, productHandle, variantId, variantPrice, sku, imageUrl);
    });

    window.UniEventBus.on('REMOVE_ACCESSORY_ITEM', (item) => {
      /**
       * item: {
       *  productId
       * }
       */
      const { productId } = item;
      this.removeItem(productId);
    });

    window.UniEventBus.on('RESET_ACCESSORIES_AFTER_ADD_TO_CART', () => {
      this.resetSelectionAfterAddToCart();
    });

    // 判断配件是否适用于目前所选择的桌子sku，会在桌子sku变化时触发
    window.UniEventBus.on('UPDATE_FIT_STATUS', (data) => {
      this.syncAccessoriesToMainVariant(data);

      const currentDeskSku = data.sku;
      let fitStatusChanged = false;
      const listItems = this.querySelectorAll('.accessories-list li[data-type=accessory]');
      for (const item of listItems) {
        const { productId } = item.dataset;
        const variantsData = document.querySelector(`#accessory-variants-data-${productId}`);
        if (variantsData) {
          const variants = JSON.parse(variantsData.innerHTML);

          // 如果配件没有任何一个sku适用于当前桌子sku，则不可选
          const unfitData = window.unfit_accessories_data || [];
          const isUnfit = variants.every((variant) =>
            unfitData.some(
              (dataItem) => dataItem.accessories_sku.includes(variant.sku) && dataItem.desks_sku.includes(currentDeskSku)
            )
          );
          const wasUnfit = item.classList.contains('unfit');
          item.classList.toggle('unfit', isUnfit);
          if (wasUnfit !== isUnfit) fitStatusChanged = true;

          if (isUnfit && this.selectedProductIds.includes(Number(productId))) {
            this.removeItem(productId);
          }
        }
      }

      if (fitStatusChanged) {
        this.updateAccessoryListImages();
      }
    });

    window.UniEventBus.registerProvider('SELECTED_ACCESSORIES', () => this.selectedAccessories);
    window.UniEventBus.registerProvider('ASSEMBLY_SERVICE_SELECTED', () => this.assemblyServiceSelected);
    window.UniEventBus.registerProvider('ACCESSORIES_BUNDLE_DISCOUNT_CONFIG', () => this.getBundleDiscountConfig());

    const initialMainVariant =
      typeof getMainProductCurrentVariant === 'function' ? getMainProductCurrentVariant() : window.currentVariant;

    if (initialMainVariant) {
      this.syncAccessoriesToMainVariant(initialMainVariant);
    }

    if (!this._initialSyncTimeout) {
      this._initialSyncTimeout = window.setTimeout(() => {
        this._initialSyncTimeout = null;
        const retryMainVariant =
          typeof getMainProductCurrentVariant === 'function' ? getMainProductCurrentVariant() : window.currentVariant;
        if (retryMainVariant) {
          this.syncAccessoriesToMainVariant(retryMainVariant);
        }
      }, 550);
    }

    this.updateOuterListOrderAndVisibility();

    if (!this._outerListResizeHandler) {
      this._outerListResizeHandler = () => this.updateOuterListOrderAndVisibility();
      window.addEventListener('resize', this._outerListResizeHandler);
    }
  }

  disconnectedCallback() {
    if (this._initialSyncTimeout) {
      window.clearTimeout(this._initialSyncTimeout);
      this._initialSyncTimeout = null;
    }
    if (this._outerListResizeHandler) {
      window.removeEventListener('resize', this._outerListResizeHandler);
      this._outerListResizeHandler = null;
    }
    console.log('disconnectedCallback');
  }

  /**
   * Hide header when modal opened
   */
  observerModal() {
    let needReveal = false;

    const observer = new MutationObserver((mutationsList, observer) => {
      const opened = mutationsList[0].target.hasAttribute('open');
      if (opened) {
        document.querySelector('.product__info-container--sticky').style.zIndex = 102;
        if (document.querySelector('.shopify-section-header-sticky:not(.shopify-section-header-hidden)')) {
          document.querySelector('sticky-header').hide();
          needReveal = true;
        }
      } else {
        document.querySelector('.product__info-container--sticky').style.zIndex = 1;
        if (needReveal) {
          document.querySelector('sticky-header').reveal();
          needReveal = false;
        }
      }
    });
    observer.observe(this.modal, { attributes: true });
  }

  /**
   * Switch to some accessory
   * @param {string} url accessory product url
   */
  displayItemAccessory(productId, url, isGift = false, oneline = false) {
    if (isGift) {
      this.contentNode.classList.add('isGift');
    } else {
      this.contentNode.classList.remove('isGift');
    }

    if (oneline) {
      this.contentNode.classList.add('oneline');
    } else {
      this.contentNode.classList.remove('oneline');
    }

    if (!this.modal.hasAttribute('open')) {
      this.modal.show();
    }

    if (this.lastFetchTask) {
      // cancel last fetch task
      this.lastFetchTask.abort();
    }

    // change list item checked style
    this.querySelectorAll('.accessories-modal-content .accessories-list li').forEach((el) => el.classList.remove('checked'));
    this.querySelector(`.accessories-modal-content .accessories-list li[data-product-id="${productId}"]`).classList.add('checked');

    this.loadingBar.style.display = 'block';
    this.contentNode.innerHTML = '';

    this.lastFetchTask = new AbortController();
    const { signal } = this.lastFetchTask;

    // 如果需要默认选中
    const matchedVariantId =
      this.matchedVariantByProductId?.[Number(productId)]?.id ||
      this.defaultSelectedVariantList?.find((item) => Number(item.productId) === Number(productId))?.variantId;

    if (matchedVariantId) {
      url = url + '&variant=' + matchedVariantId;
    }

    fetch(url, { signal })
      .then((r) => r.text())
      .then((res) => {
        // delete last fetch task
        this.lastFetchTask = null;
        this.loadingBar.style.display = 'none';
        this.contentNode.innerHTML = res;
      })
      .catch((e) => {
        if (e.name === 'AbortError') {
          console.warn('fetch canceled');
        }
      });
  }
}

customElements.define('accessories-sales', AccessoriesSales);
